import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Minus, Plus, Info, Check, Copy, CalendarDays, Mail } from 'lucide-react';
import { toast } from 'sonner';

import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const CONTACT_EMAIL = 'hello@xchannels.ai';

const nf = (n, lang) => Number(n || 0).toLocaleString(lang === 'de' ? 'de-DE' : 'en-GB');
const eur = (n, lang) => `${nf(n, lang)} €`;

const START_SELECTION = { linkedin: 3, instagram: 2 };   // Beispiel zum Anpassen

/** Text der Anfrage (E-Mail-Entwurf und Kopie). */
const buildRequestText = ({ result, cycle, user, lang }) => {
  const T = (de, en) => (lang === 'de' ? de : en);
  const plan = result.plan;
  const isCustom = plan.family === 'custom';
  const lines = result.lines.map((l) => {
    const unit = l.unit === 'week' ? T('pro Woche', 'per week') : T('pro Monat', 'per month');
    return `- ${l.label}: ${l.count} ${unit} (${l.posts_per_month} ${T('Beiträge pro Monat', 'posts per month')}, ${l.credits} Credits)`;
  });
  let price = '';
  if (!isCustom) {
    price = cycle === 'yearly'
      ? T(`jährlich: ${eur(plan.price.yearly_per_month, lang)} pro Monat, ${eur(plan.price.yearly_total, lang)} pro Jahr, zzgl. USt.`,
          `yearly: ${eur(plan.price.yearly_per_month, lang)} per month, ${eur(plan.price.yearly_total, lang)} per year, excl. VAT`)
      : T(`monatlich: ${eur(plan.price.monthly, lang)} pro Monat, zzgl. USt.`, `monthly: ${eur(plan.price.monthly, lang)} per month, excl. VAT`);
  }
  const subject = isCustom
    ? T('Anfrage Custom-Paket', 'Custom plan enquiry')
    : T(`Paket-Anfrage: ${plan.label} (${cycle === 'yearly' ? 'jährlich' : 'monatlich'})`, `Plan request: ${plan.label} (${cycle === 'yearly' ? 'yearly' : 'monthly'})`);
  const body = [
    T('Hallo xchannels.ai Team,', 'Hello xchannels.ai team,'),
    '',
    isCustom ? T('ich interessiere mich für ein individuelles Angebot.', 'I am interested in an individual offer.')
             : T('ich möchte folgendes Paket abonnieren:', 'I would like to subscribe to the following plan:'),
    '',
    `${T('Paket', 'Plan')}: ${plan.label}`,
    ...(isCustom ? [] : [`${T('Abrechnung', 'Billing')}: ${price}`, `${T('Credits pro Monat im Paket', 'Credits per month in plan')}: ${plan.credits}`]),
    `${T('Benötigte Credits laut Konfigurator', 'Credits needed according to the configurator')}: ${result.total_credits} (${T('inkl.', 'incl.')} ${result.reserve_percent} % ${T('Reserve für Änderungen', 'reserve for edits')})`,
    '',
    `${T('Meine Kanäle', 'My channels')}:`,
    ...lines,
    '',
    `${T('Konto', 'Account')}: ${user?.email || ''}${user?.name ? ` (${user.name})` : ''}`,
    '',
    isCustom ? T('Bitte meldet euch bei mir für ein Gespräch.', 'Please get in touch to schedule a call.')
             : T('Bitte schickt mir die Rechnung bzw. den Zahlungslink.', 'Please send me the invoice or payment link.'),
    '',
    T('Viele Grüße', 'Kind regards'),
  ].join('\n');
  return { subject, body };
};

const Upgrade = () => {
  const { lang } = useLanguage();
  const { user, getAuthHeader } = useAuth();
  const T = (de, en) => (lang === 'de' ? de : en);

  const [config, setConfig] = React.useState(null);
  const [selection, setSelection] = React.useState(START_SELECTION);
  const [result, setResult] = React.useState(null);
  const [cycle, setCycle] = React.useState('yearly');
  const [sent, setSent] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);
  const calcSeq = React.useRef(0);

  React.useEffect(() => {
    axios.get(`${API}/api/billing/configurator`).then((r) => setConfig(r.data)).catch(() => setLoadError(true));
  }, []);

  // Berechnung am Server (eine Rechenregel für alles), leicht verzögert
  React.useEffect(() => {
    const seq = ++calcSeq.current;
    const timer = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/api/billing/configurator/calc`, { selection });
        if (seq === calcSeq.current) setResult(r.data);
      } catch (e) { /* alte Anzeige bleibt */ }
    }, 200);
    return () => clearTimeout(timer);
  }, [selection]);

  const change = (key, delta, max) => {
    setSent(false);
    setSelection((cur) => {
      const next = Math.max(0, Math.min(max, (cur[key] || 0) + delta));
      const copy = { ...cur };
      if (next === 0) delete copy[key]; else copy[key] = next;
      return copy;
    });
  };

  const unitLabel = (ch, n) => {
    if (ch.key === 'youtube') return T(n === 1 ? 'Video pro Monat' : 'Videos pro Monat', n === 1 ? 'video per month' : 'videos per month');
    if (ch.key === 'pressemitteilung') return T(n === 1 ? 'Pressemitteilung pro Monat' : 'Pressemitteilungen pro Monat', n === 1 ? 'press release per month' : 'press releases per month');
    if (ch.unit === 'week') {
      if (n >= 14) return T('pro Woche (2× täglich)', 'per week (twice daily)');
      if (n === 7) return T('pro Woche (täglich)', 'per week (daily)');
      return T('pro Woche', 'per week');
    }
    return T('pro Monat', 'per month');
  };

  const plan = result?.plan;
  const request = plan ? buildRequestText({ result, cycle, user, lang }) : null;
  const mailto = request ? `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(request.subject)}&body=${encodeURIComponent(request.body)}` : '#';

  const recordRequest = () => {
    // Anfrage intern festhalten (Berechnung kommt dort erneut vom Server); stört den E-Mail-Entwurf nicht
    axios.post(`${API}/api/billing/plan-request`, { selection, cycle }, { headers: getAuthHeader() }).catch(() => {});
  };

  const subscribe = () => {
    recordRequest();
    setSent(true);
    window.location.href = mailto;
  };

  const copyRequest = async () => {
    try {
      await navigator.clipboard.writeText(`${CONTACT_EMAIL}\n\n${request.subject}\n\n${request.body}`);
      toast.success(T('Anfrage kopiert. Schick sie an hello@xchannels.ai.', 'Request copied. Send it to hello@xchannels.ai.'));
    } catch (e) {
      toast.error(T('Kopieren nicht möglich. Schreib uns direkt an hello@xchannels.ai.', 'Could not copy. Please write to hello@xchannels.ai.'));
    }
  };

  const stepBtn = 'w-9 h-9 rounded bg-[#1A2B42] border border-[#2D3F59] text-white flex items-center justify-center hover:border-[#00D9C8]/60 disabled:opacity-30 disabled:hover:border-[#2D3F59]';

  return (
    <div className="min-h-full bg-[#0A1628] text-white p-4 lg:p-8" data-testid="upgrade-page">
      <div className="max-w-6xl mx-auto">
        <Link to="/app/kreation" className="inline-flex items-center gap-2 text-[#A0AEC0] hover:text-white text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> {T('Zurück', 'Back')}
        </Link>

        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{T('Dein Paket konfigurieren', 'Configure your plan')}</h1>
        <p className="text-[#A0AEC0] text-sm sm:text-base mb-8 max-w-2xl">
          {T('Wähle deine Kanäle und wie oft du sie bespielen willst. Wir berechnen die Credits und zeigen dir das passende Paket.',
             'Choose your channels and how often you want to publish. We calculate the credits and show you the matching plan.')}
        </p>

        {loadError && <div className="rounded-lg bg-[#3B1F1F] text-[#F5B7B1] px-4 py-3 text-sm mb-6">{T('Der Konfigurator konnte nicht geladen werden. Schreib uns an hello@xchannels.ai.', 'The configurator could not be loaded. Please write to hello@xchannels.ai.')}</div>}

        <div className="grid lg:grid-cols-[1fr_380px] gap-6 items-start">
          {/* Kanäle */}
          <div>
            <div className="flex items-start gap-2 text-sm text-[#8B9BB0] mb-3" data-testid="image-note">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{T('Zu jedem Posting gehört ein Bild (außer beim Newsletter). Auf die berechneten Credits legen wir 10 % Reserve für Änderungen.',
                       'Every post comes with an image (except the newsletter). We add a 10% reserve on the calculated credits for edits.')}</span>
            </div>
            <div className="space-y-2">
              {(config?.channels || []).map((ch) => {
                const n = selection[ch.key] || 0;
                return (
                  <div key={ch.key} className={`flex items-center justify-between gap-3 flex-wrap p-4 rounded-lg border transition-colors ${n > 0 ? 'bg-[#1A2B42] border-[#00D9C8]/40' : 'bg-[#0F1F35] border-[#1F2F45]'}`} data-testid={`cfg-channel-${ch.key}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-white">{ch.label}</div>
                      <div className="text-xs text-[#6B7A94]">{ch.credits_per_post} {T('Credits je Beitrag', 'credits per post')}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-[#A0AEC0] text-right min-w-[120px]">{n > 0 ? `${n} ${unitLabel(ch, n)}` : T('nicht dabei', 'not included')}</span>
                      <button className={stepBtn} onClick={() => change(ch.key, -1, ch.max)} disabled={n === 0} aria-label="weniger" data-testid={`cfg-minus-${ch.key}`}><Minus className="w-4 h-4" /></button>
                      <button className={stepBtn} onClick={() => change(ch.key, 1, ch.max)} disabled={n >= ch.max} aria-label="mehr" data-testid={`cfg-plus-${ch.key}`}><Plus className="w-4 h-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-sm text-[#8B9BB0] mt-5" data-testid="agency-note">
              {T('Agentur? Schreibe uns unter ', 'Agency? Write to us at ')}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#00D9C8] hover:underline">{CONTACT_EMAIL}</a>
            </p>
          </div>

          {/* Ergebnis */}
          <div className="lg:sticky lg:top-6">
            <div className="rounded-xl bg-[#1A2B42] border border-[#2D3F59] p-5" data-testid="cfg-result">
              {!plan ? (
                <div className="text-sm text-[#8B9BB0] py-6 text-center">{T('Wähle mindestens einen Kanal, dann siehst du dein Paket.', 'Choose at least one channel to see your plan.')}</div>
              ) : (
                <>
                  <div className="text-xs uppercase tracking-wide text-[#00D9C8] font-semibold mb-1">{T('Dein Paket', 'Your plan')}</div>
                  <div className="text-3xl font-black mb-1" data-testid="cfg-plan-label">{plan.label}</div>

                  {plan.family !== 'custom' ? (
                    <>
                      <div className="inline-flex rounded-lg bg-[#0A1628] p-1 my-3" role="tablist">
                        {[['monthly', T('Monatlich', 'Monthly')], ['yearly', T('Jährlich', 'Yearly')]].map(([key, label]) => (
                          <button key={key} onClick={() => { setCycle(key); setSent(false); }} role="tab" aria-selected={cycle === key}
                            className={`px-4 py-1.5 text-sm rounded-md font-semibold transition-colors ${cycle === key ? 'bg-[#00D9C8] text-[#0A1628]' : 'text-[#A0AEC0] hover:text-white'}`}>{label}</button>
                        ))}
                      </div>
                      <div className="flex items-baseline gap-2" data-testid="cfg-price">
                        <span className="text-4xl font-black">{eur(cycle === 'yearly' ? plan.price.yearly_per_month : plan.price.monthly, lang)}</span>
                        <span className="text-sm text-[#A0AEC0]">{T('pro Monat zzgl. USt.', 'per month excl. VAT')}</span>
                      </div>
                      {cycle === 'yearly' && <div className="text-xs text-[#6B7A94] mt-1">{T(`Jährliche Zahlung: ${eur(plan.price.yearly_total, lang)} zzgl. USt.`, `Billed yearly: ${eur(plan.price.yearly_total, lang)} excl. VAT`)}</div>}
                    </>
                  ) : (
                    <div className="text-sm text-[#B7C4D4] mt-2 mb-1">{T('Für deine Auswahl machen wir dir ein individuelles Angebot.', 'For your selection we will make you an individual offer.')}</div>
                  )}

                  {(result.reasons || []).length > 0 && (
                    <ul className="mt-4 space-y-1.5" data-testid="cfg-reasons">
                      {result.reasons.map((r) => (
                        <li key={r.code} className="flex items-start gap-2 text-sm text-[#F5D78E]"><Info className="w-4 h-4 mt-0.5 shrink-0" />{lang === 'de' ? r.de : r.en}</li>
                      ))}
                    </ul>
                  )}

                  {/* Aufschlüsselung */}
                  <div className="mt-5 pt-4 border-t border-[#2D3F59] text-sm space-y-1.5" data-testid="cfg-breakdown">
                    {result.lines.map((l) => (
                      <div key={l.channel} className="flex justify-between gap-3">
                        <span className="text-[#A0AEC0]">{l.label} <span className="text-[#6B7A94]">({l.posts_per_month}×)</span></span>
                        <span className="text-white font-medium">{nf(l.credits, lang)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between gap-3">
                      <span className="text-[#A0AEC0]">{T('Reserve für Änderungen', 'Reserve for edits')} ({result.reserve_percent} %)</span>
                      <span className="text-white font-medium">{nf(result.reserve_credits, lang)}</span>
                    </div>
                    <div className="flex justify-between gap-3 pt-2 border-t border-[#2D3F59]">
                      <span className="font-semibold">{T('Credits pro Monat', 'Credits per month')}</span>
                      <span className="font-black text-white" data-testid="cfg-total">{nf(result.total_credits, lang)}</span>
                    </div>
                    {plan.credits && (
                      <div className="pt-1">
                        <div className="h-1.5 bg-[#0A1628] rounded overflow-hidden"><div className="h-full bg-[#00D9C8]" style={{ width: `${Math.min(100, Math.round((result.utilization || 0) * 100))}%` }} /></div>
                        <div className="text-xs text-[#6B7A94] mt-1">{T(`${nf(result.total_credits, lang)} von ${nf(plan.credits, lang)} Credits im Paket genutzt`, `${nf(result.total_credits, lang)} of ${nf(plan.credits, lang)} plan credits used`)}</div>
                      </div>
                    )}
                  </div>

                  {/* Abschluss */}
                  <div className="mt-5 space-y-2">
                    {plan.family === 'custom' ? (
                      <>
                        <a href={config?.booking_url || '#'} target="_blank" rel="noopener noreferrer"
                          className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628] font-black" data-testid="cfg-book-call">
                          <CalendarDays className="w-5 h-5" />{T('Gespräch buchen', 'Book a call')}
                        </a>
                        <button onClick={subscribe} className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[#2D3F59] text-[#A0AEC0] hover:text-white hover:border-[#00D9C8]/50 text-sm font-semibold" data-testid="cfg-mail-custom">
                          <Mail className="w-4 h-4" />{T('Per E-Mail anfragen', 'Enquire by email')}
                        </button>
                      </>
                    ) : (
                      <button onClick={subscribe} className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628] font-black text-base" data-testid="cfg-subscribe">
                        <Check className="w-5 h-5" />{T('Jetzt abonnieren', 'Subscribe now')}
                      </button>
                    )}
                    {sent && (
                      <div className="text-xs text-[#8B9BB0] leading-relaxed" data-testid="cfg-sent-note">
                        {T('Dein E-Mail-Programm öffnet einen fertigen Entwurf an ', 'Your email app opens a ready draft to ')}<b className="text-white">{CONTACT_EMAIL}</b>{T('. Du musst ihn nur noch abschicken. Öffnet sich nichts: ', '. You only need to send it. If nothing opens: ')}
                        <button onClick={copyRequest} className="inline-flex items-center gap-1 text-[#00D9C8] hover:underline"><Copy className="w-3 h-3" />{T('Anfrage kopieren', 'copy the request')}</button>
                      </div>
                    )}
                    <p className="text-xs text-[#6B7A94]">{T('Wir schalten dein Paket nach der Zahlung frei.', 'We activate your plan after payment.')}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Upgrade;
